// src/utils/assetFieldMetadata.js
//
// Canonical image-field ↔ asset metadata helpers.
//
// Purpose:
// - Keep form image fields aligned with the asset document schema.
// - Support Standard DAM-lite assets: icon, banner, screenshot.
// - Support Pro/Enterprise Asset Library assets and versioned assets.
// - Preserve durable Storage paths separately from browser URLs.
// - Remain backward-compatible with legacy assetReference string values.

const PUBLIC_FORM_ASSET_TYPES = new Set([
  'icon',
  'banner',
  'screenshot',
]);

const IMAGE_FIELD_TYPES = new Set([
  'icon',
  'banner',
]);

export const isPlainObject = (value) =>
  Boolean(value) &&
  typeof value === 'object' &&
  !Array.isArray(value);

export const isBrowserUrl = (value) =>
  typeof value === 'string' &&
  (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('blob:') ||
    value.startsWith('data:')
  );

const cleanString = (value) => {
  if (value == null) return null;
  const next = String(value).trim();
  return next || null;
};

export const normalizeSystemAssetType = (value) => {
  const raw = cleanString(value);
  if (!raw) return null;

  const slug = raw.toLowerCase().replace(/[\s_-]+/g, '');

  if (slug === 'formpreview' || slug === 'formpreviewimage') {
    return 'screenshot';
  }

  if (slug === 'preview' || slug === 'screenshot') {
    return 'screenshot';
  }

  if (slug === 'icon') return 'icon';
  if (slug === 'banner') return 'banner';

  return raw.toLowerCase();
};

export const getAssetReferenceId = (assetReference) => {
  if (!assetReference) return null;

  if (typeof assetReference === 'string') {
    return cleanString(assetReference);
  }

  if (!isPlainObject(assetReference)) return null;

  return (
    cleanString(assetReference.assetId) ||
    cleanString(assetReference.id) ||
    cleanString(assetReference.assetReference) ||
    null
  );
};

const readAssetId = (asset) => {
  if (!asset) return null;

  if (typeof asset === 'string') {
    return cleanString(asset);
  }

  if (!isPlainObject(asset)) return null;

  return (
    cleanString(asset.assetId) ||
    cleanString(asset.id) ||
    getAssetReferenceId(asset.assetReference) ||
    null
  );
};

const readVersionNumber = (asset, existingReference, options = {}) => {
  const candidates = [
    options.versionNumber,
    asset?.versionNumber,
    asset?.assetReference?.versionNumber,
    asset?.assetReference?.version,
    existingReference?.versionNumber,
    existingReference?.version,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  return null;
};

const readAssetType = (asset, existingMetadata = {}, options = {}) => {
  const candidates = [
    options.assetType,
    asset?.assetType,
    asset?.systemAssetType,
    asset?.assetReference?.assetType,
    asset?.assetReference?.systemAssetType,
    existingMetadata?.assetType,
    existingMetadata?.systemAssetType,
    existingMetadata?.assetReference?.assetType,
    existingMetadata?.assetReference?.systemAssetType,
    Array.isArray(existingMetadata?.allowedImageTypes)
      ? existingMetadata.allowedImageTypes[0]
      : null,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeSystemAssetType(candidate);
    if (normalized) return normalized;
  }

  return null;
};

const normalizeAllowedImageTypes = (value, fallback = 'icon') => {
  const rawValues = Array.isArray(value) ? value : [value];

  const selected = rawValues
    .map(normalizeSystemAssetType)
    .find((type) => IMAGE_FIELD_TYPES.has(type));

  if (selected) return [selected];

  const fallbackType = normalizeSystemAssetType(fallback);
  return [IMAGE_FIELD_TYPES.has(fallbackType) ? fallbackType : 'icon'];
};

const normalizeDimensions = (asset, existingMetadata = {}, options = {}) => {
  const source =
    options.dimensions ||
    asset?.dimensions ||
    asset?.metadata?.dimensions ||
    asset?.assetReference?.dimensions ||
    existingMetadata?.dimensions ||
    null;

  const width =
    Number(source?.width) ||
    Number(asset?.versions?.original?.width) ||
    Number(existingMetadata?.versions?.original?.width) ||
    null;

  const height =
    Number(source?.height) ||
    Number(asset?.versions?.original?.height) ||
    Number(existingMetadata?.versions?.original?.height) ||
    null;

  if (!width || !height) return null;

  return { width, height };
};

const normalizeVersions = (asset, existingMetadata = {}) => {
  const versions =
    asset?.versions ||
    asset?.assetReference?.versions ||
    existingMetadata?.versions ||
    null;

  if (!isPlainObject(versions)) return null;

  return { ...versions };
};

const normalizeThumb = (asset, existingMetadata = {}) => {
  const thumb =
    asset?.thumb ||
    asset?.assetReference?.thumb ||
    existingMetadata?.thumb ||
    null;

  if (!isPlainObject(thumb)) return null;

  const urls = isPlainObject(thumb.urls) ? { ...thumb.urls } : undefined;
  const micro = isPlainObject(thumb.micro) ? { ...thumb.micro } : undefined;

  return {
    ...thumb,
    ...(urls ? { urls } : {}),
    ...(micro ? { micro } : {}),
  };
};

const pickThumbnailPath = ({ asset, existingMetadata, versions, thumb }) => {
  const candidates = [
    versions?.thumbnail?.path,
    thumb?.urls?.['400'],
    thumb?.urls?.['800'],
    asset?.thumbnail,
    existingMetadata?.thumbnail,
  ];

  return candidates.map(cleanString).find(Boolean) || null;
};

const pickThumbUrl = ({ asset, existingMetadata, thumbnail }) => {
  const candidates = [
    asset?.thumbUrl,
    asset?.previewUrl,
    asset?.grid?.u400,
    asset?.grid?.u800,
    asset?.cloudSrc,
    asset?.assetReference?.thumbUrl,
    existingMetadata?.thumbUrl,
    isBrowserUrl(thumbnail) ? thumbnail : null,
  ];

  return candidates
    .map(cleanString)
    .find((value) => value && isBrowserUrl(value)) || null;
};

export const normalizeAssetReference = (
  asset,
  existingReference = null,
  options = {},
) => {
  const assetId =
    readAssetId(asset) ||
    getAssetReferenceId(existingReference);

  if (!assetId) return null;

  const assetType = readAssetType(asset, {}, options);
  const systemAssetType =
    normalizeSystemAssetType(asset?.systemAssetType) ||
    normalizeSystemAssetType(assetType) ||
    null;

  const organizationId =
    cleanString(options.organizationId) ||
    cleanString(asset?.organizationId) ||
    cleanString(asset?.assetReference?.organizationId) ||
    cleanString(existingReference?.organizationId) ||
    null;

  const versionNumber = readVersionNumber(
    asset,
    existingReference,
    options,
  );

  return {
    assetId,
    assetType: assetType || null,
    systemAssetType,
    versionNumber,
    organizationId,
    storageProvider: 'firebase',
    source: options.source || 'asset-library',
    ...(asset?.previousAssetId
      ? { previousAssetId: asset.previousAssetId }
      : {}),
  };
};

export const normalizeImageFieldMetadata = (
  existingMetadata = {},
  asset = null,
  options = {},
) => {
  const safeExisting = isPlainObject(existingMetadata)
    ? existingMetadata
    : {};

  const existingReference = safeExisting.assetReference || null;

  const assetType =
    readAssetType(asset, safeExisting, options) ||
    normalizeSystemAssetType(
      Array.isArray(safeExisting.allowedImageTypes)
        ? safeExisting.allowedImageTypes[0]
        : null,
    ) ||
    'icon';

  const allowedImageTypes = normalizeAllowedImageTypes(
    options.allowedImageTypes || safeExisting.allowedImageTypes,
    IMAGE_FIELD_TYPES.has(assetType) ? assetType : 'icon',
  );

  const assetReference = normalizeAssetReference(
    asset || { assetReference: existingReference },
    existingReference,
    {
      ...options,
      assetType,
    },
  );

  const versions = normalizeVersions(asset, safeExisting);
  const thumb = normalizeThumb(asset, safeExisting);
  const dimensions = normalizeDimensions(asset, safeExisting, options);

  const thumbnail = pickThumbnailPath({
    asset,
    existingMetadata: safeExisting,
    versions,
    thumb,
  });

  const thumbUrl = pickThumbUrl({
    asset,
    existingMetadata: safeExisting,
    thumbnail,
  });

  return {
    ...safeExisting,
    type: 'image',
    allowedImageTypes,
    assetReference,
    versions,
    dimensions,
    thumbnail,
    thumb,
    thumbUrl,
  };
};

export const clearImageFieldAssetMetadata = (existingMetadata = {}) => {
  const safeExisting = isPlainObject(existingMetadata)
    ? existingMetadata
    : {};

  return {
    ...safeExisting,
    assetReference: null,
    thumbnail: null,
    versions: null,
    thumb: null,
    thumbUrl: null,
    dimensions: null,
  };
};

export const normalizeImageFieldsMetadataMap = (fieldsMetadata = {}) => {
  if (!isPlainObject(fieldsMetadata)) return {};

  let changed = false;
  const next = {};

  for (const [fieldId, metadata] of Object.entries(fieldsMetadata)) {
    if (metadata?.type === 'image') {
      const normalized = normalizeImageFieldMetadata(metadata);
      next[fieldId] = normalized;

      if (normalized !== metadata) changed = true;
    } else {
      next[fieldId] = metadata;
    }
  }

  return changed ? next : fieldsMetadata;
};

export const areAssetReferencesEqual = (a, b) => {
  const aId = getAssetReferenceId(a);
  const bId = getAssetReferenceId(b);

  if (aId !== bId) return false;

  const aVersion = Number(a?.versionNumber || a?.version || 0);
  const bVersion = Number(b?.versionNumber || b?.version || 0);

  if (aVersion !== bVersion) return false;

  const aType = normalizeSystemAssetType(a?.assetType || a?.systemAssetType);
  const bType = normalizeSystemAssetType(b?.assetType || b?.systemAssetType);

  return (aType || null) === (bType || null);
};

export const isPublicFormAssetType = (assetType) =>
  PUBLIC_FORM_ASSET_TYPES.has(normalizeSystemAssetType(assetType));

const sanitizeStoragePathValue = (value) => {
  const cleaned = cleanString(value);
  if (!cleaned) return null;
  return isBrowserUrl(cleaned) ? null : cleaned;
};

const sanitizeStoragePathMap = (value) => {
  if (!isPlainObject(value)) return undefined;

  const next = {};

  Object.entries(value).forEach(([key, rawPath]) => {
    const cleaned = sanitizeStoragePathValue(rawPath);
    if (cleaned) {
      next[key] = cleaned;
    }
  });

  return Object.keys(next).length > 0 ? next : undefined;
};

const sanitizeVersionsForPersistence = (versions = null) => {
  if (!isPlainObject(versions)) return versions;

  const next = {};

  Object.entries(versions).forEach(([versionKey, versionNode]) => {
    if (!isPlainObject(versionNode)) {
      next[versionKey] = versionNode;
      return;
    }

    const cleanedPath = sanitizeStoragePathValue(versionNode.path);

    next[versionKey] = {
      ...versionNode,
      ...(cleanedPath ? { path: cleanedPath } : {}),
    };

    if (versionNode.path && !cleanedPath) {
      delete next[versionKey].path;
    }
  });

  return next;
};

const sanitizeThumbForPersistence = (thumb = null) => {
  if (!isPlainObject(thumb)) return thumb;

  const urls = sanitizeStoragePathMap(thumb.urls);
  const micro = sanitizeStoragePathMap(thumb.micro);

  const {
    urls: _rawUrls,
    micro: _rawMicro,
    thumbUrl: _runtimeThumbUrl,
    previewUrl: _runtimePreviewUrl,
    grid: _runtimeGrid,
    cloudSrc: _runtimeCloudSrc,
    localSrc: _runtimeLocalSrc,
    ...rest
  } = thumb;

  return {
    ...rest,
    ...(urls ? { urls } : {}),
    ...(micro ? { micro } : {}),
  };
};

export const sanitizeImageFieldMetadataForPersistence = (
  existingMetadata = {},
) => {
  const normalized = normalizeImageFieldMetadata(existingMetadata);

  const {
    thumbUrl: _runtimeThumbUrl,
    previewUrl: _runtimePreviewUrl,
    grid: _runtimeGrid,
    cloudSrc: _runtimeCloudSrc,
    localSrc: _runtimeLocalSrc,
    ...rest
  } = normalized;

  const next = { ...rest };

  // Do not persist browser URLs as durable thumbnail paths.
  if (next.thumbnail && isBrowserUrl(next.thumbnail)) {
    next.thumbnail = null;
  }

  // Defensive cleanup: versions.*.path and thumb maps should contain
  // durable Storage object paths, never signed URLs, blob URLs, or data URLs.
  next.versions = sanitizeVersionsForPersistence(next.versions);
  next.thumb = sanitizeThumbForPersistence(next.thumb);

  // Asset reference should stay durable and portable across envs.
  if (isPlainObject(next.assetReference)) {
    const {
      thumbUrl: _assetRefThumbUrl,
      previewUrl: _assetRefPreviewUrl,
      grid: _assetRefGrid,
      cloudSrc: _assetRefCloudSrc,
      localSrc: _assetRefLocalSrc,
      thumb: _assetRefThumb,
      versions: _assetRefVersions,
      ...durableAssetReference
    } = next.assetReference;

    next.assetReference = durableAssetReference;
  }

  return next;
};

export const sanitizeImageFieldsMetadataMapForPersistence = (
  fieldsMetadata = {},
) => {
  if (!isPlainObject(fieldsMetadata)) return {};

  let changed = false;
  const next = {};

  for (const [fieldId, metadata] of Object.entries(fieldsMetadata)) {
    if (metadata?.type === 'image') {
      const sanitized = sanitizeImageFieldMetadataForPersistence(metadata);
      next[fieldId] = sanitized;

      if (sanitized !== metadata) changed = true;
    } else {
      next[fieldId] = metadata;
    }
  }

  return changed ? next : fieldsMetadata;
};